import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import PlanHeader from './PlanHeader'
import PlanFilterPills from './filters/PlanFilterPills'
import PlanCard from './board/PlanCard'
import PlanCategoryGroup from './board/PlanCategoryGroup'
import PlanItemDetailPanel from './detailPanel/PlanItemDetailPanel'
import boardStyles from './board/styles'
import { applyStatusFilter, categoryHasMatchingItem, countTotalItems, deriveFilterPills, findNode } from './helpers'

const PANEL_TRANSITION_MS = 300
const DEFAULT_PANEL_WIDTH = 600

const useStyles = makeStyles((theme) => ({
  bottomRow: {
    display: 'flex',
    gap: 'var(--dim-4)',
    alignItems: 'flex-start',
    width: '100%'
  },
  panelWrapper: ({ panelWidth }) => ({
    position: 'sticky',
    top: 'var(--dim-4)',
    alignSelf: 'flex-start',
    flex: `0 0 ${panelWidth}px`,
    maxWidth: `${panelWidth}px`,
    transform: 'translateX(100%)',
    opacity: 0,
    transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
    '&.__visible': {
      transform: 'translateX(0)',
      opacity: 1
    }
  }),
  report: {
    width: '100%',
    gap: 'var(--dim-4)',
    display: 'flex',
    flexDirection: 'column',
    ...boardStyles(theme)
  },
  emptyFiltered: {
    minHeight: '120px',
    color: 'var(--text-color-subtle)'
  }
}))

function PlanBoard ({ plan, config, displayName }) {
  const panelWidth = Number.isFinite(config?.panelWidth) && config.panelWidth > 0
    ? config.panelWidth
    : DEFAULT_PANEL_WIDTH
  const classes = useStyles({ panelWidth })
  const [currentFilter, setCurrentFilter] = useState(null)
  const [selectedItemId, setSelectedItemId] = useState(null)
  const [panelVisible, setPanelVisible] = useState(false)
  const closeTimeoutRef = useRef(null)

  const statusStyleOverrides = config?.statusStyleOverrides
  const taskComponent = config?.taskComponent

  const pills = useMemo(() => deriveFilterPills(plan, statusStyleOverrides), [plan, statusStyleOverrides])
  const totalCount = useMemo(() => countTotalItems(plan), [plan])
  const filteredPlan = useMemo(() => applyStatusFilter(plan, currentFilter), [plan, currentFilter])
  const selected = useMemo(() => findNode(plan, selectedItemId), [plan, selectedItemId])

  const handleClose = useCallback(() => {
    setPanelVisible(false)
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null
      setSelectedItemId(null)
    }, PANEL_TRANSITION_MS)
  }, [])

  useEffect(() => () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
  }, [])

  const handleSelectItem = useCallback((itemId) => {
    if (itemId === selectedItemId) {
      handleClose()
      return
    }
    setSelectedItemId(itemId)
  }, [selectedItemId, handleClose])

  useEffect(() => {
    if (!selectedItemId) return
    requestAnimationFrame(() => setPanelVisible(true))
  }, [selectedItemId])

  useEffect(() => {
    if (selectedItemId && !findNode(plan, selectedItemId)) {
      handleClose()
    }
  }, [plan, selectedItemId, handleClose])

  useEffect(() => {
    if (!selectedItemId) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedItemId, handleClose])

  return (
    <div className='flex flex-col w-full'>
      <PlanHeader
        plan={plan}
        title={config?.title || plan?.name || displayName}
        statusStyleOverrides={statusStyleOverrides}
      />
      <PlanFilterPills
        pills={pills}
        totalCount={totalCount}
        currentFilter={currentFilter}
        onFilter={setCurrentFilter}
        allItemsLabel={config?.allItemsLabel || 'All Statuses'}
      />
      <div className={classes.bottomRow}>
        <div className='flex-1' style={{ minWidth: 0 }}>
          <div className={classes.report}>
            <PlanCard
              plan={plan}
              cardLabel={config?.planCardLabel || 'The Plan'}
              statusStyleOverrides={statusStyleOverrides}
            >
              {filteredPlan?.data?.length
                ? filteredPlan.data.map(category => (
                  <PlanCategoryGroup
                    key={category.id}
                    category={category}
                    statusStyleOverrides={statusStyleOverrides}
                    selectedItemId={selectedItemId}
                    onSelectItem={handleSelectItem}
                    defaultExpanded={categoryHasMatchingItem(category, currentFilter)}
                  />
                ))
                : (
                  <div className={`${classes.emptyFiltered} flex items-center justify-center font-size-sm`}>
                    No items match this filter
                  </div>
                )}
            </PlanCard>
          </div>
        </div>

        {selected ? (
          <div className={clsx(classes.panelWrapper, { __visible: panelVisible })}>
            <PlanItemDetailPanel
              item={selected.node}
              category={selected.kind === 'item' ? selected.parent : null}
              statusStyleOverrides={statusStyleOverrides}
              taskComponent={taskComponent}
              onClose={handleClose}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}

PlanBoard.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    metadata: PropTypes.object,
    data: PropTypes.array
  }).isRequired,
  config: PropTypes.object,
  displayName: PropTypes.string
}

export default PlanBoard
